// The household app's own cylinder (apps/consumer BottleGauge), as the
// approved design ports it, and the small brand cylinder of the orders table.
import { useId } from 'react';

const MINI_BODY = 'M16 14 Q16 22 6 28 Q2 29 2 33 L2 108 Q2 116 10 116 L46 116 Q54 116 54 108 L54 33 Q54 29 50 28 Q40 22 40 14 Z';
const gasVar = (l: number) => (l < 20 ? 'var(--red)' : 'var(--blue)');

export function MiniBottle({ level, w = 28, anim = false }: { level: number; w?: number; anim?: boolean }) {
  const id = 'mb' + useId().replace(/:/g, '');
  const c = gasVar(level);
  const p = Math.max(0, Math.min(100, level)) / 100;
  const fy = 115 - p * 89;
  const cr = fy - 2;
  const h = (w * 120) / 56;
  const wave = `M-56 ${cr} Q-42 ${cr - 3} -28 ${cr} T0 ${cr} T28 ${cr} T56 ${cr} T84 ${cr} T112 ${cr} V120 H-56 Z`;
  return (
    <svg width={w} height={h} viewBox="0 0 56 120" aria-hidden="true" style={{ flex: 'none', overflow: 'visible' }}>
      <defs>
        <clipPath id={id}><path d={MINI_BODY} /></clipPath>
        <linearGradient id={id + 'g'} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" style={{ stopColor: c, stopOpacity: 0.85 }} /><stop offset="1" style={{ stopColor: c }} />
        </linearGradient>
      </defs>
      <g clipPath={`url(#${id})`}>
        <rect width="56" height="120" style={{ fill: 'var(--cyl-in)' }} />
        <rect x="0" y={fy} width="56" height={120 - fy} fill={`url(#${id}g)`} />
        <path d={wave} style={{ fill: c }} opacity=".4" className={anim ? 'wv' : ''} />
        {anim && level > 10 && [0, 1, 2].map((i) => (
          <circle key={i} cx={16 + i * 12} cy={Math.min(110, fy + 30)} r={2 + i * 0.6} fill="#fff" className="bub" style={{ animationDelay: `${i * 0.7}s` }} />
        ))}
        <rect x="42" y="34" width="3" height="46" rx="1.5" style={{ fill: 'var(--cyl-glint)' }} />
      </g>
      <path d="M14 0h6v8h-6zM36 0h6v8h-6z" fill="none" style={{ stroke: 'var(--cyl-line)' }} strokeWidth="1.2" />
      <rect x="20" y="2" width="16" height="12" rx="3" fill="none" style={{ stroke: 'var(--cyl-line)' }} strokeWidth="1.2" />
      <path d={MINI_BODY} fill="none" style={{ stroke: 'var(--cyl-line)' }} strokeWidth="1.6" />
    </svg>
  );
}

const lum = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
};

export function BrandCyl({ color, h = 40 }: { color: string; h?: number }) {
  const light = lum(color) > 0.72;
  const s = light ? 'rgba(15,23,42,.28)' : 'none';
  const mark = light ? 'rgba(15,23,42,.35)' : '#fff'; // white marks vanish on a pale brand colour
  return (
    <svg width={h * 0.6} height={h} viewBox="0 0 60 100" aria-hidden="true" style={{ flex: 'none' }}>
      <g fill={color} stroke={s} strokeWidth="1">
        <path d="M18 7a3 3 0 013-3h4v18h-7zM35 4h4a3 3 0 013 3v15h-7z" />
        <rect x="27" y="8" width="6" height="14" rx="1" /><rect x="15" y="23" width="30" height="4" rx="1.5" />
        <path d="M10 40a11 11 0 0111-11h18a11 11 0 0111 11v46a10 10 0 01-10 10H20a10 10 0 01-10-10z" />
        <rect x="8" y="44" width="44" height="4" rx="1.5" /><rect x="8" y="84" width="44" height="4" rx="1.5" />
      </g>
      <circle cx="30" cy="14" r="1.6" fill={mark} opacity=".85" />
      <rect x="41" y="52" width="3" height="28" rx="1.5" fill={mark} opacity=".65" />
    </svg>
  );
}

const BIG_BODY = 'M68 32 Q68 48 30 60 Q10 66 10 80 L10 320 Q10 348 38 348 L162 348 Q190 348 190 320 L190 80 Q190 66 170 60 Q132 48 132 32 Z';

// The household app's big cylinder (BigBottle), for one home's hero card.
export function BigBottle({ level, w = 150 }: { level: number; w?: number }) {
  const id = 'bb' + useId().replace(/:/g, '');
  const c = gasVar(level);
  const p = Math.max(0, Math.min(100, level)) / 100;
  const fy = 345 - p * 271;
  const h = (w * 360) / 200;
  let wv1 = `M-200 ${fy - 3}`;
  for (let x = -200; x < 400; x += 50) wv1 += ` Q${x + 25} ${fy - 8} ${x + 50} ${fy - 3}`;
  wv1 += ' V360 H-200 Z';
  let wv2 = `M-200 ${fy}`;
  for (let x = -200; x < 400; x += 60) wv2 += ` Q${x + 30} ${fy - 6} ${x + 60} ${fy}`;
  wv2 += ' V360 H-200 Z';
  const line = { stroke: 'var(--cyl-line)' };
  return (
    <svg width={w} height={h} viewBox="0 0 200 360" aria-hidden="true" style={{ overflow: 'visible' }}>
      <defs>
        <clipPath id={id}><path d={BIG_BODY} /></clipPath>
        <linearGradient id={id + 'g'} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" style={{ stopColor: c, stopOpacity: 0.72 }} /><stop offset=".5" style={{ stopColor: c, stopOpacity: 0.86 }} /><stop offset="1" style={{ stopColor: c }} />
        </linearGradient>
      </defs>
      <g clipPath={`url(#${id})`}>
        <rect width="200" height="360" style={{ fill: 'var(--cyl-in)' }} />
        <rect x="0" y={fy} width="200" height={360 - fy} fill={`url(#${id}g)`} />
        <path d={wv1} style={{ fill: c }} opacity=".35" className="wv2" />
        <path d={wv2} style={{ fill: c }} opacity=".2" className="wv3" />
        {[25, 50, 75].map((q) => { const gy = 345 - (q / 100) * 271; return <path key={q} d={`M10 ${gy}H28M172 ${gy}H190`} style={{ stroke: 'var(--cyl-mark)' }} strokeWidth="1.2" />; })}
        {level > 10 && [0, 1, 2, 3, 4].map((i) => (
          <circle key={i} cx={50 + i * 24} cy={Math.min(330, fy + 60 + i * 8)} r={3 + (i % 3)} fill="#fff" className="bub" style={{ animationDelay: `${i * 0.55}s` }} />
        ))}
        <rect x="155" y="70" width="6" height="180" rx="3" style={{ fill: 'var(--cyl-glint)' }} className="glint" />
      </g>
      <rect x="60" y="0" width="14" height="22" rx="4" fill="none" style={line} strokeWidth="1.6" />
      <rect x="126" y="0" width="14" height="22" rx="4" fill="none" style={line} strokeWidth="1.6" />
      <rect x="72" y="4" width="56" height="28" rx="6" fill="none" style={line} strokeWidth="1.6" />
      <path d={BIG_BODY} fill="none" style={line} strokeWidth="1.8" />
    </svg>
  );
}
